import { useLocation } from "react-router-dom";
import { Home, FolderOpen, MessagesSquare, Search, type LucideIcon } from "lucide-react";
import { useRouter } from "@/hooks/useRouter";

interface SlotConfig {
  key: string;
  label: string;
  icon: LucideIcon;
  href: string;
  /** Match the route exactly instead of by prefix (used for "/"). */
  exact?: boolean;
}

const PILL_SLOTS: SlotConfig[] = [
  { key: "home", label: "Home", icon: Home, href: "/", exact: true },
  { key: "projects", label: "Projects", icon: FolderOpen, href: "/projects" },
  { key: "chats", label: "Chats", icon: MessagesSquare, href: "/chats" },
  { key: "search", label: "Search", icon: Search, href: "/search" },
];

function slotIsActive(slot: SlotConfig, pathname: string): boolean {
  if (slot.exact) return pathname === slot.href;
  return pathname === slot.href || pathname.startsWith(slot.href + "/");
}

/**
 * Mobile floating bottom chrome — a 4-slot pill (Home / Projects / Chats /
 * Search) plus a circular menu button that opens MobileMenuSheet. The active
 * highlight is a single absolutely-positioned span that translates between
 * slots so only one ever appears selected and the motion is a slide rather
 * than a crossfade. Unlike the desktop NavigationRibbon this is route-based:
 * slots navigate and derive active state from the current location.
 */
export function MobileBottomNav({
  menuOpen,
  onToggleMenu,
}: {
  menuOpen: boolean;
  onToggleMenu: () => void;
}) {
  const { pathname } = useLocation();
  const router = useRouter();

  const activeIndex = PILL_SLOTS.findIndex((s) => slotIsActive(s, pathname));

  return (
    <div
      // `absolute` against the shell's fixed, isolated root — not `fixed`
      // against the window — so the dock's position and paint order depend
      // only on the shell, never on routed content or ancestor transforms.
      className="pointer-events-none absolute inset-x-0 bottom-0 z-50 flex items-stretch gap-2.5 px-3"
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
    >
      <nav
        aria-label="Main navigation"
        // Fully opaque (no translucency/backdrop-blur) so the pill stays a
        // clean surface regardless of what scrolls underneath it.
        className="pointer-events-auto relative flex-1 flex items-stretch rounded-full bg-card border border-border shadow-lg p-1"
      >
        <span
          aria-hidden
          // Sliding active highlight — brand fill + a thin inset ring so the
          // active slot reads even over busy content.
          className={`absolute top-1 bottom-1 rounded-full bg-brand/10 ring-1 ring-inset ring-brand/20 transition-[opacity,transform] duration-300 ease-out pointer-events-none ${
            activeIndex < 0 ? "opacity-0" : "opacity-100"
          }`}
          style={{
            left: "0.25rem",
            width: `calc((100% - 0.5rem) / ${PILL_SLOTS.length})`,
            transform: `translateX(${Math.max(activeIndex, 0) * 100}%)`,
          }}
        />

        {PILL_SLOTS.map((slot) => {
          const Icon = slot.icon;
          const isActive = slotIsActive(slot, pathname);
          return (
            <button
              key={slot.key}
              type="button"
              onClick={() => router.push(slot.href)}
              aria-current={isActive ? "page" : undefined}
              aria-label={slot.label}
              className="relative flex-1 min-w-0 inline-flex flex-col items-center justify-center gap-0.5 h-12 px-1 rounded-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <Icon
                size={20}
                strokeWidth={1.75}
                className={`transition-colors duration-200 ${
                  isActive ? "text-brand" : "text-muted-foreground"
                }`}
              />
              <span
                className={`text-[10px] leading-none truncate max-w-full px-1 transition-colors duration-200 ${
                  isActive ? "text-brand font-semibold" : "text-muted-foreground font-medium"
                }`}
              >
                {slot.label}
              </span>
            </button>
          );
        })}
      </nav>

      <button
        type="button"
        onClick={onToggleMenu}
        aria-label={menuOpen ? "Close menu" : "Open menu"}
        aria-expanded={menuOpen}
        aria-controls="mobile-menu-sheet"
        aria-haspopup="dialog"
        // `scale-[0.94]` when open ties the button visually to the sheet's
        // morph: the sheet blooms outward from this point and the button
        // depresses slightly as if "consumed" by the expansion.
        className={`pointer-events-auto flex-shrink-0 h-14 w-14 rounded-full border shadow-lg inline-flex items-center justify-center cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand transition-[background-color,border-color,color,transform] duration-300 ease-out ${
          menuOpen
            ? "bg-brand/10 border-brand/30 text-brand scale-[0.94]"
            : "bg-card border-border text-muted-foreground hover:text-foreground scale-100"
        }`}
      >
        {/* Hamburger ↔ X morph: three SVG lines. Top + bottom translate to
            the center and rotate ±45°; the middle squeezes out. Motion spec
            lives in the .hamburger-bar rules in index.css. */}
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          className="w-5 h-5"
          aria-hidden="true"
          data-menu-open={menuOpen}
        >
          <line className="hamburger-bar hamburger-bar-top" x1="3" y1="6" x2="21" y2="6" />
          <line className="hamburger-bar hamburger-bar-mid" x1="3" y1="12" x2="21" y2="12" />
          <line className="hamburger-bar hamburger-bar-bot" x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>
    </div>
  );
}
